"use client";

import { BookingSection } from "@/components/booking/BookingSection";
import { I18nProvider } from "@/components/booking/I18nProvider";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslation } from "react-i18next";

type BookingType = "private" | "company" | null;

export default function BookingsClient({ lang }: { lang?: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { i18n } = useTranslation();

  // Debug logging for hydration issue
  if (typeof window !== "undefined") {
    console.log("[BookingsClient] Client-side render:", {
      propLang: lang,
      i18nLanguage: i18n.language,
      i18nResolvedLanguage: i18n.resolvedLanguage,
      searchParamLang: searchParams.get("lang"),
      isClient: true,
      timestamp: new Date().toISOString()
    });
  } else {
    console.log("[BookingsClient] Server-side render:", {
      propLang: lang,
      i18nLanguage: i18n.language,
      i18nResolvedLanguage: i18n.resolvedLanguage,
      searchParamLang: searchParams.get("lang"),
      isClient: false,
      timestamp: new Date().toISOString()
    });
  }

  const handleContinue = (type: BookingType) => {
    // Priority order:
    // 1. URL parameter (explicit user choice)
    // 2. i18n resolved language (from detection/localStorage)
    // 3. i18n current language
    // 4. English (ultimate fallback)
    // Note: Removed browser language detection here to avoid hydration mismatches
    // The I18nProvider already handles browser language detection properly in useEffect
    const urlLang = searchParams.get("lang");
    const langValue = urlLang ||
      i18n.resolvedLanguage ||
      i18n.language ||
      "en";
    const lang = (langValue || "en").split("-")[0].toLowerCase();

    console.log("[BookingsClient] handleContinue called:", {
      type,
      urlLang,
      i18nResolvedLanguage: i18n.resolvedLanguage,
      i18nLanguage: i18n.language,
      computedLangValue: langValue,
      finalLang: lang,
      isClient: typeof window !== "undefined"
    });

    router.push(`/bookings/venues?type=${type}&lang=${lang}`);
  };

  return (
    <I18nProvider lang={lang}>
      <BookingSection onContinue={handleContinue} />
    </I18nProvider>
  );
}
